import React, { useRef, useEffect, useState } from "react";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { DirectionControllerService } from "../../services";

interface Section {
  label: string;
  startAngle: number;
  endAngle: number;
  subSections: string[];
}

const Compass: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  // const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [hoveredSubSection, setHoveredSubSection] = useState<string | null>(
    null
  );

  const [sections, setSections] = useState<Section[]>([]);
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("xs"));
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isLg = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const canvasSize = isXs ? 200 : isSm ? 300 : isMd ? 40 : isLg ? 450 : 700;
  const radius = canvasSize / 2 - 20;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response =
          await DirectionControllerService.directionControllerGetAllDirections();
        const fetchedSections = response
          .filter((item: any) => item.displayName !== "Bramhasthan")
          .map((item) => ({
            label: item.displayName,
            startAngle: item.start,
            endAngle: item.end,
            subSections: item.entrances,
          }));
        setSections(fetchedSections);
      } catch (error) {
        console.error("Error fetching compass data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const width = canvas.width;
        const height = canvas.height;
        // const radius = 350;
        const angleOffset = -90;

        const drawCompass = () => {
          ctx.clearRect(0, 0, width, height);
          ctx.beginPath();
          ctx.arc(width / 2, height / 2, radius - 20, 0, Math.PI * 2);
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = "grey";
          ctx.stroke();
          for (let i = 0; i < 360; i += 5) {
            const angle = ((i + angleOffset) * Math.PI) / 180;
            const x1 = width / 2 + (radius - 10) * Math.cos(angle);
            const y1 = height / 2 + (radius - 10) * Math.sin(angle);
            const x2 = width / 2 + radius * Math.cos(angle);
            const y2 = height / 2 + radius * Math.sin(angle);

            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);

            if (i % 10 === 0) {
              ctx.lineWidth = 1;
            } else {
              ctx.lineWidth = 0.5;
            }
            ctx.stroke();

            if (i % 30 === 0) {
              const labelX = width / 2 + (radius + 30) * Math.cos(angle);
              const labelY = height / 2 + (radius + 30) * Math.sin(angle);

              ctx.font = " bold 12px Arial";
              ctx.fillStyle = "black";
              ctx.textAlign = "center";
              ctx.strokeStyle = "#fff";
              ctx.lineWidth = 3;
              ctx.strokeText(i.toString(), labelX, labelY);
              ctx.fillText(i.toString(), labelX, labelY);
            }
          }

          ctx.beginPath();
          ctx.arc(width / 2, height / 2, 5, 0, Math.PI * 2);
          ctx.fillStyle = "#FF0000";
          ctx.fill();
        };

        const drawSections = () => {
          sections.forEach(({ label, startAngle, endAngle, subSections }) => {
            const startRad = ((startAngle + angleOffset) * Math.PI) / 180;
            const endRad = ((endAngle + angleOffset) * Math.PI) / 180;
            // const midAngle = (startRad + endRad) / 2;
            const totalSectionDegrees =
              endAngle >= startAngle
                ? endAngle - startAngle
                : endAngle + 360 - startAngle;
            const midAngle = (startAngle + totalSectionDegrees / 2) % 360;
            const midRad = ((midAngle + angleOffset) * Math.PI) / 180;

            ctx.beginPath();
            ctx.moveTo(width / 2, height / 2);
            ctx.arc(width / 2, height / 2, radius, startRad, endRad);
            ctx.lineTo(width / 2, height / 2);
            ctx.fillStyle =
              hoveredSection === label && !hoveredSubSection
                ? "rgba(173, 216, 230, 0.3)"
                : "transparent";
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.stroke();

            const labelX = width / 2 + (radius + 40) * Math.cos(midRad);
            const labelY = height / 2 + (radius + 40) * Math.sin(midRad);
            ctx.font = " bold 16px Arial";
            ctx.fillStyle = "black";
            ctx.textAlign = "center";
            ctx.strokeStyle = "#fff";
            ctx.lineWidth = 3;
            ctx.strokeText(label, labelX, labelY);
            ctx.fillText(label, labelX, labelY);

            const sectionAngleRange =
              endAngle >= startAngle
                ? endAngle - startAngle
                : endAngle + 360 - startAngle;
            const subSectionAngleRange = sectionAngleRange / subSections.length;

            subSections.forEach((subSection, subIndex) => {
              const subStartAngle =
                startAngle + subIndex * subSectionAngleRange + angleOffset;
              const subEndAngle = subStartAngle + subSectionAngleRange;
              const subMidAngle =
                (((subStartAngle + subEndAngle) / 2) * Math.PI) / 180;

              ctx.beginPath();
              ctx.moveTo(width / 2, height / 2);
              ctx.arc(
                width / 2,
                height / 2,
                radius - 50,
                (subStartAngle * Math.PI) / 180,
                (subEndAngle * Math.PI) / 180
              );
              ctx.lineTo(width / 2, height / 2);
              ctx.fillStyle =
                hoveredSubSection === subSection
                  ? "rgba(144, 238, 144, 0.3)"
                  : "transparent";
              ctx.fill();
              ctx.lineWidth = 0.5;
              ctx.stroke();

              const subLabelX =
                width / 2 + (radius - 70) * Math.cos(subMidAngle);
              const subLabelY =
                height / 2 + (radius - 70) * Math.sin(subMidAngle);
              ctx.font = " bold 12px Arial";
              ctx.fillStyle = "black";
              ctx.textAlign = "center";
              ctx.strokeStyle = "#fff";
              ctx.lineWidth = 3;
              ctx.strokeText(subSection, subLabelX, subLabelY);
              ctx.fillText(subSection, subLabelX, subLabelY);
            });
          });
        };

        drawCompass();
        drawSections();
      }
    }
  }, [hoveredSection, hoveredSubSection, sections]);

  const getSectionByAngle = (angle: number) => {
    const normalizedAngle = (angle + 360) % 360;
    return (
      sections.find(({ startAngle, endAngle }) => {
        if (startAngle < endAngle) {
          return normalizedAngle >= startAngle && normalizedAngle <= endAngle;
        } else {
          return normalizedAngle >= startAngle || normalizedAngle <= endAngle;
        }
      })?.label || null
    );
  };

  const getSubSectionByAngle = (angle: number, mainLabel: string) => {
    const section = sections.find((section) => section.label === mainLabel);
    if (section) {
      const { startAngle, endAngle, subSections } = section;
      const sectionAngleRange =
        endAngle >= startAngle
          ? endAngle - startAngle
          : endAngle + 360 - startAngle;
      const subSectionAngleRange = sectionAngleRange / subSections.length;

      for (let i = 0; i < subSections.length; i++) {
        const subStartAngle = startAngle + i * subSectionAngleRange;
        const subEndAngle = subStartAngle + subSectionAngleRange;
        if (
          (subStartAngle < subEndAngle &&
            angle >= subStartAngle &&
            angle <= subEndAngle) ||
          (subStartAngle > subEndAngle &&
            (angle >= subStartAngle || angle <= subEndAngle))
        ) {
          return subSections[i];
        }
      }
    }
    return null;
  };
  const angleOffset = -270;
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const mouseX = (e.clientX - rect.left) * scaleX;
      const mouseY = (e.clientY - rect.top) * scaleY;

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      let angle =
        (Math.atan2(mouseY - centerY, mouseX - centerX) * 180) / Math.PI;

      angle += angleOffset;

      const normalizedAngle = (angle + 360) % 360;

      const distanceFromCenter = Math.sqrt(
        Math.pow(mouseX - centerX, 2) + Math.pow(mouseY - centerY, 2)
      );
      const innerRadius = 160;
      const outerRadius = 350;

      let sectionLabel = null;
      let subSectionLabel = null;

      if (distanceFromCenter <= outerRadius) {
        sectionLabel = getSectionByAngle(normalizedAngle);
        if (sectionLabel && distanceFromCenter <= innerRadius) {
          subSectionLabel = getSubSectionByAngle(normalizedAngle, sectionLabel);
        }
      }

      setHoveredSection(sectionLabel);
      setHoveredSubSection(subSectionLabel);
    }
  };

  // const handleClick = () => {
  //     setSelectedSection(hoveredSubSection || hoveredSection);
  // };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <canvas
        ref={canvasRef}
        width={900}
        height={900}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          setHoveredSection(null);
          setHoveredSubSection(null);
        }}
        // onClick={handleClick}
      />
    </Box>
  );
};

export default Compass;
